/**
 * Resolves the credit balance to show and to gate report creation on.
 *
 * `GET /auth/user` carries two balances:
 *
 * - `credits_minimun` — the *effective* balance, min(DB, Snap API), computed by
 *   `CreditsService.compare_user_credits_with_api_credits`. `null` means the
 *   Snap balance is unknown (no key, or the credits call failed); `0` is a real
 *   zero.
 * - `credits` — the DB balance. Stays `NULL` for users that were never seeded.
 *
 * When Snap rejected the API key (`api_key_invalid`) nothing is spendable, so
 * the DB balance must not be shown — that mismatch is what made users see
 * credits while every create failed with "chave de API inválida".
 */
export interface UserCreditsSource {
    credits?: number | null;
    credits_minimun?: number | null;
    api_key_invalid?: boolean;
}

const isBalance = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value) && value >= 0;

export function resolveUserCredits(
    source: UserCreditsSource | null | undefined
): number {
    if (source?.api_key_invalid) {
        return 0;
    }
    if (isBalance(source?.credits_minimun)) {
        return source.credits_minimun;
    }
    if (isBalance(source?.credits)) {
        return source.credits;
    }
    return 0;
}
